import { onBeforeUnmount, ref } from "vue";

import {
  SERVER_ERROR_STATUS,
  STATUS_POLL_INTERVAL_MS,
} from "../constants/payment";
import {
  getOrderStatus,
  isTerminalStatus,
  ORDER_STATUS,
} from "../markets/shared/orderStatus";
import { fetchOrderData, getGuidFromUrl } from "../services/orderApi";

export function useOrderStatus({ onTerminal, onServerError } = {}) {
  const orderStatus = ref(ORDER_STATUS.PENDING);
  const lastRawStatus = ref(null);

  let timeoutId = null;
  let stopped = false;
  let inFlight = false;

  function clearScheduledPoll() {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  }

  function stop() {
    stopped = true;
    clearScheduledPoll();
  }

  function applyStatus(rawSlug) {
    lastRawStatus.value = rawSlug ?? null;
    const next = getOrderStatus({ status: { slug: rawSlug } });
    const prev = orderStatus.value;
    orderStatus.value = next;

    if (isTerminalStatus(next) && next !== prev) {
      stop();
      onTerminal?.(next);
    }
  }

  function setFromOrder(order) {
    applyStatus(order?.status?.slug);
  }

  function scheduleNext() {
    if (stopped) return;
    if (isTerminalStatus(orderStatus.value)) return;

    clearScheduledPoll();
    timeoutId = window.setTimeout(pollOnce, STATUS_POLL_INTERVAL_MS);
  }

  async function pollOnce() {
    if (stopped || inFlight) return;
    if (isTerminalStatus(orderStatus.value)) return;

    const guid = getGuidFromUrl();
    if (!guid) return;

    inFlight = true;
    try {
      const order = await fetchOrderData(guid);
      applyStatus(order?.status?.slug);
    } catch (error) {
      if ((error?.status ?? 0) >= SERVER_ERROR_STATUS) {
        stop();
        onServerError?.(error);
        return;
      }
    } finally {
      inFlight = false;
      scheduleNext();
    }
  }

  function startPolling() {
    if (stopped) return;
    if (isTerminalStatus(orderStatus.value)) return;
    if (!getGuidFromUrl()) return;

    scheduleNext();
  }

  onBeforeUnmount(stop);

  return {
    orderStatus,
    lastRawStatus,
    setOrderStatusFromOrder: setFromOrder,
    startStatusPolling: startPolling,
    stopStatusPolling: stop,
  };
}
